import { Component, computed, DestroyRef, inject, OnInit, output, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  DEFAULT_PHONE_NUMBER_VALUE,
  OAUTH_SUCCESS_STORAGE_KEY,
  OTP_LENGTH,
  STATUS_CODES,
} from '../../constants';
import { finalize } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  AvailableLoginTypeDto,
  LoginConfirmDto,
  LoginRequestDto,
  OtpRequestDto,
  OtpDestinationType,
} from '@api/models';
import { AuthService } from '../../services/auth.service';
import { AuthSyncService } from '../../services/auth-sync.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxMaskDirective } from 'ngx-mask';
import { Subscription } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { OtpComponent } from '../otp/otp.component';
import { ResetPasswordComponent } from '../reset-password/reset-password.component';
import { FormRadioInputComponent } from '../form-radio-input/form-radio-input.component';
import { ButtonComponent } from '../button/button.component';
import { CustomerService } from '../../services/customer.service';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';

export enum LoginType {
  Password = 'password',
  Otp = 'otp',
}

class LoginForm {
  login: FormControl<string>;
  password: FormControl<string>;
  otpDestinationType: FormControl<OtpDestinationType>;
  otpDigits: FormArray<FormControl<string>>;
}

type LoginFormState = 'login-request' | 'types-selection' | 'otp' | 'login-password' | 'reset-password-login' | 'reset-password-confirm';

@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    NgxMaskDirective,
    FormsModule,
    IconComponent,
    NgTemplateOutlet,
    OtpComponent,
    ResetPasswordComponent,
    FormRadioInputComponent,
    ButtonComponent,
  ],
})
export class LoginComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly authSyncService = inject(AuthSyncService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly customerService = inject(CustomerService);

  otpComponent = viewChild(OtpComponent);
  resetPasswordComponent = viewChild(ResetPasswordComponent);

  changed = output<void>();
  loggedIn = output<void>();

  form: FormGroup<LoginForm>;
  state = signal<LoginFormState>('login-request');
  isLoading = signal<boolean>(false);
  allAvailableLoginTypes = signal<AvailableLoginTypeDto[]>([]);
  notSelectedAvailableLoginTypes = computed<AvailableLoginTypeDto[]>(() => {
    return this.allAvailableLoginTypes().filter(loginTypeDto => {
      if (this.state() === 'login-password') {
        return loginTypeDto.loginType !== LoginType.Password;
      } else {
        return loginTypeDto.otpDestinationType !== this.sentOtpRequest()?.otpDestinationType;
      }
    });
  });
  sentOtpRequest = signal<OtpRequestDto>(null);
  error = signal<string>(null);
  selectedLoginTypeIndexControl = new FormControl<number>(0);
  private isFormErrorsVisible = signal<boolean>(false);

  private pendingHttpRequestSub: Subscription;

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm() {
    const otpDigitControls: FormControl<string>[] = new Array(OTP_LENGTH)
      .fill(null)
      .map(() => this.formBuilder.control('', Validators.required));

    this.form = this.formBuilder.group<LoginForm>({
      login: this.formBuilder.control('', Validators.required),
      otpDestinationType: this.formBuilder.control(null),
      password: this.formBuilder.control('', Validators.required),
      otpDigits: this.formBuilder.array(otpDigitControls),
    });
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearErrors();
        this.changed.emit();
      });

    this.forcePhoneNumberToDefaultIfApplicable();
  }

  changeState(newState: LoginFormState): void {
    this.pendingHttpRequestSub?.unsubscribe();
    this.clearErrors();
    this.selectedLoginTypeIndexControl.setValue(0);
    this.state.set(newState);
  }

  submit(): void {
    this.clearErrors();
    if (this.isLoading()) {
      return;
    }

    switch (this.state()) {
      case 'login-request':
        this.loginRequest();
        break;
      case 'otp':
      case 'login-password':
        this.loginConfirm();
        break;
      case 'types-selection':
        const selectedLoginType = this.allAvailableLoginTypes()[this.selectedLoginTypeIndexControl.getRawValue()];
        this.loginByType(selectedLoginType);
        break;
      case 'reset-password-login':
        this.state.set('reset-password-confirm');
        break;
    }
  }

  loginRequest(): void {
    this.clearErrors();

    if (this.form.controls.login.invalid) {
      this.isFormErrorsVisible.set(true);
      return;
    }

    const dto: LoginRequestDto = {
      login: this.form.getRawValue().login,
      otpDestinationType: this.form.getRawValue().otpDestinationType,
    };
    this.isLoading.set(true);
    this.pendingHttpRequestSub = this.authService.login(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.allAvailableLoginTypes.set(response.availableLoginTypes);
          this.sentOtpRequest.set(response.sentOtpRequest);
          this.changeState(this.sentOtpRequest() ? 'otp' : 'types-selection');
        },
        error: error => {
          const errorMessage = resolveHttpErrorMessage(error);

          if (error.status === STATUS_CODES.tooManyRequests) {
            const otpRequest = error.error?.otpRequest;
            if (otpRequest) {
              this.sentOtpRequest.set(otpRequest);
              this.changeState('otp');
              return;
            }

            const resetErrorMs = 10 * 1000; // 10 sec
            setTimeout(() => {
              this.error.update(currentError => currentError === errorMessage ? null : currentError);
            }, resetErrorMs);
          }

          this.error.set(errorMessage);
        },
      });
  }

  loginConfirm(): void {
    this.clearErrors();

    switch (this.state()) {
      case 'otp':
        if (this.form.controls.otpDigits.invalid) {
          this.otpComponent().isFormErrorsVisible.set(true);
          this.otpComponent().focusFirstInvalidOtpInput();
          return;
        }
        break;
      case 'login-password':
        if (this.form.controls.login.invalid || this.form.controls.password.invalid) {
          this.isFormErrorsVisible.set(true);
          return;
        }
        break;
      default:
        return;
    }

    const dto: LoginConfirmDto = {
      login: this.form.getRawValue().login,
    };

    switch (this.state()) {
      case 'otp':
        dto.otpConfirm = {
          otpCode: this.form.controls.otpDigits.getRawValue().join(''),
          requestId: this.sentOtpRequest()?.requestId,
        };
        break;
      case 'login-password':
        dto.password = this.form.getRawValue().password;
        break;
    }

    this.isLoading.set(true);
    this.pendingHttpRequestSub = this.authService.loginConfirm(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.loggedIn.emit(),
        error: error => this.error.set(resolveHttpErrorMessage(error)),
      });
  }

  loginByType(loginTypeDto: AvailableLoginTypeDto): void {
    this.clearErrors();
    this.form.controls.otpDigits.reset();
    this.form.controls.otpDestinationType.setValue(loginTypeDto.otpDestinationType);

    switch (loginTypeDto.loginType) {
      case LoginType.Password:
        this.sentOtpRequest.set(null);
        this.changeState('login-password');
        break;
      case LoginType.Otp:
        this.loginRequest();
        break;
    }
  }

  resendOtp(): void {
    this.clearErrors();
    this.form.controls.otpDestinationType.setValue(this.sentOtpRequest().otpDestinationType);

    this.loginRequest();
  }

  isControlInvalid(control: AbstractControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  isPhoneLogin(): boolean {
    const login = this.form.getRawValue().login;
    return login.startsWith(DEFAULT_PHONE_NUMBER_VALUE) || login.startsWith(DEFAULT_PHONE_NUMBER_VALUE.slice(1));
  }

  private forcePhoneNumberToDefaultIfApplicable(): void {
    this.form.controls.login.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        value = value.trim().replaceAll(/\+/g, '');
        const isNumeric = /^\d+$/.test(value);
        if (!isNumeric || value.length < 3) {
          return;
        }
        const validPrefix = DEFAULT_PHONE_NUMBER_VALUE.slice(1);
        for (let i = validPrefix.length - 1; i >= 1; i--) {
          const currentPrefix = validPrefix.slice(i);
          if (value.startsWith(currentPrefix)) {
            const resolvedPrefix = validPrefix.slice(0, i);
            this.form.controls.login.setValue(`${resolvedPrefix}${value}`);
            return;
          }
        }
      });
  }

  back(): void {
    this.changeState('login-request');
    this.allAvailableLoginTypes.set([]);
    this.sentOtpRequest.set(null);
    this.form.controls.otpDigits.reset();
    this.form.controls.otpDestinationType.reset();
  }

  loginByOauth(provider: 'google' | 'facebook') {
    this.clearErrors();

    const url = this.authService.getSocialLoginUrl(provider);
    window.open(url, '', "menubar=0,toolbar=0,status=0,width=626,height=436");

    const interval = setInterval(() => {
      const isSuccess = localStorage.getItem(OAUTH_SUCCESS_STORAGE_KEY) === 'true';
      if (!isSuccess) {
        return;
      }

      localStorage.removeItem(OAUTH_SUCCESS_STORAGE_KEY);

      this.customerService.fetchAndSetCustomer()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
          response => {
            if (response) {
              this.authSyncService.notifySignedIn();
              this.loggedIn.emit();
            }
          }
        );

      clearInterval(interval);
    }, 100);
  }

  private clearErrors(): void {
    this.isFormErrorsVisible.set(false);
    this.error.set(null);
  }

  protected readonly LoginType = LoginType;

  getTypedLoginTypeDto(loginTypeDto: unknown): AvailableLoginTypeDto {
    return loginTypeDto as AvailableLoginTypeDto;
  }
}
